import { useEffect } from 'react';

import { useThread } from '@frontend/hooks/useThread';

type ThreadUpdatedEventPayload = {
  threadId?: string | null;
};

// Might change in future
type ThreadDeletedEventPayload = ThreadUpdatedEventPayload;

const EVENT_NAME = {
  OPAL_THREAD_CREATED: 'opal::thread::created',
  OPAL_THREAD_CHANGED: 'opal::thread::changed',
  OPAL_THREAD_DELETED: 'opal::thread::deleted',
  OPAL_CHAT_SET_THREAD: 'opal::chat::setThread',
};

function isThreadUpdatedEvent(event: Event): event is CustomEvent<ThreadUpdatedEventPayload> {
  return (
    event instanceof CustomEvent &&
    (event.type === EVENT_NAME.OPAL_THREAD_CHANGED || event.type === EVENT_NAME.OPAL_THREAD_CREATED)
  );
}

function isThreadDeletedEvent(event: Event): event is CustomEvent<ThreadDeletedEventPayload> {
  return event instanceof CustomEvent && event.type === EVENT_NAME.OPAL_THREAD_DELETED;
}

export function ChatEventListener({ children }: { children: React.ReactNode }) {
  const { threadId, setThreadId } = useThread();

  useEffect(() => {
    const handleThreadUpdated = (event: Event) => {
      if (isThreadUpdatedEvent(event)) {
        const { threadId: newThreadId } = event.detail;
        setThreadId(newThreadId);
      }
    };
    const handleThreadDeleted = (event: Event) => {
      if (isThreadDeletedEvent(event)) {
        const { threadId: deletedThreadId } = event.detail;
        if (threadId === deletedThreadId) {
          setThreadId(undefined);
        }
      }
    };

    window.addEventListener(EVENT_NAME.OPAL_THREAD_CHANGED, handleThreadUpdated);
    window.addEventListener(EVENT_NAME.OPAL_THREAD_CREATED, handleThreadUpdated);
    window.addEventListener(EVENT_NAME.OPAL_THREAD_DELETED, handleThreadDeleted);

    return () => {
      window.removeEventListener(EVENT_NAME.OPAL_THREAD_CHANGED, handleThreadUpdated);
      window.removeEventListener(EVENT_NAME.OPAL_THREAD_CREATED, handleThreadUpdated);
      window.removeEventListener(EVENT_NAME.OPAL_THREAD_DELETED, handleThreadDeleted);
    };
  }, [threadId, setThreadId]);

  useEffect(() => {
    document.dispatchEvent(
      new CustomEvent(EVENT_NAME.OPAL_CHAT_SET_THREAD, {
        detail: { threadId },
        bubbles: true,
      })
    );
  }, [threadId]);

  return <>{children}</>;
}
